import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaTimes, FaPlus, FaEdit, FaTrash, FaSave, FaBan, FaSpinner, FaInfoCircle, FaSortAmountDown } from 'react-icons/fa';
import { useToast } from '../../hooks/useToast';
import { apiService } from '../../services/apiService';
import useLookupOptions from '../../hooks/useLookupOptions';
import CampiOperatoriSubEditor, { OPERATORI_VALIDI } from './CampiOperatoriSubEditor';
import OrdinamentoCampiModal from '../Macrofunzioni/OrdinamentoCampiModal';
import {
    buildCampiOperatoriRequestParams,
    normalizeCampiOperatoriResponse,
} from './campiOperatoriAdapter';

/**
 * Modale dedicata per i campi delle tabelle del modulo HUB / MILO
 * (`SKE_Tabelle.TAB_Id_Moduli === 15`).
 *
 * NON sostituisce `CampiTabellaModal` standard: viene instradata
 * condizionalmente dal componente padre. Questa modale espone solo i
 * metadati realmente consumati dal generatore TypeScript Milo
 * (vedi ADR-013 / ADR-014):
 *
 *   - Niente `CAM_AutoIncrement`, `CAM_Like`, `CAM_AccettaZero`,
 *     `CAM_Scollegato`, `CAM_ListaInFiltro`, `CAM_Id_TabelleFK`,
 *     `CAM_NomeFK`, `CAM_Suffisso`.
 *   - Aggiunge i 6 campi Milo: `CAM_ChiaveNaturale`, `CAM_Cercabile`,
 *     `CAM_Versioned`, `CAM_Cifrato`, `CAM_NomeUi`, `CAM_ErroreValidazione`.
 *   - Sub-editor inline `SKE_CampiOperatori` quando `CAM_Cercabile = 1`.
 *
 * Convenzione `CAM_Sequenza`:
 *   - 1..1000  = campo dominio (emesso nei template UI Milo)
 *   - > 1000   = campo tecnico (audit / SCD2)
 */

const TIPI_CON_LUNGHEZZA = new Set(['varchar', 'char', 'decimal']);
const SNAKE_CASE_RE = /^[a-z][a-z0-9_]*$/;

const HARDCODED_DEFAULTS = {
    CAM_Incrementale: 0,
    CAM_Like: 0,
    CAM_AccettaZero: 0,
    CAM_Scollegato: 0,
    CAM_ListaInFiltro: 2,           // 'Singolo', come la modale standard
    CAM_Id_TabelleFK: null,
    CAM_NomeCampoChiaveEsterno: null,
    CAM_Suffisso: null,
};

const emptyForm = () => ({
    CAM_Id_Campi: null,
    CAM_Nome: '',
    CAM_Descrizione: '',
    CAM_Id_TipiCampi: '',
    CAM_Lunghezza: '',
    CAM_Sequenza: '',
    CAM_Nullo: false,
    CAM_Default: '',
    CAM_ChiaveNaturale: false,
    CAM_Cercabile: false,
    CAM_Versioned: true,
    CAM_Cifrato: false,
    CAM_NomeUi: '',
    CAM_ErroreValidazione: '',
});

const toBoolFlag = (v) => (v === true || v === 1 || v === '1' ? 1 : 0);

const CampiTableModalHub = ({
    isOpen,
    onClose,
    currentTableId,
    initialCampi = [],
    onCampiUpdated,
}) => {
    const { showSuccess, showError } = useToast();

    const [displayCampi, setDisplayCampi] = useState([]);
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(emptyForm());
    const [operatoriRows, setOperatoriRows] = useState([]); // righe SKE_CampiOperatori del campo in editing
    const [originalOperatoriIds, setOriginalOperatoriIds] = useState([]); // per calcolare DELETE
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingOperatori, setIsLoadingOperatori] = useState(false);
    const [isOrdinamentoModalOpen, setIsOrdinamentoModalOpen] = useState(false);

    const lookupConfig = useMemo(() => ({
        tipiCampiLookup: {
            tableName: 'SKE_TipiCampi',
            valueField: 'TIP_Id_TipiCampi',
            labelField: 'TIP_Descrizione',
            additionalFields: ['TIP_CodiceTipoSql', 'TIP_CodiceTipoDrizzle'],
        },
    }), []);
    const { lookupOptions, loading: isLoadingLookups } = useLookupOptions(lookupConfig);

    // Solo tipi con TIP_CodiceTipoDrizzle valorizzato (richiesta MILO).
    const tipiCampiOptions = useMemo(() => {
        const all = lookupOptions?.tipiCampiLookup || [];
        return all.filter((t) => {
            const drizzle = t?.original?.TIP_CodiceTipoDrizzle;
            return drizzle !== null && drizzle !== undefined && String(drizzle).trim() !== '';
        });
    }, [lookupOptions]);

    const tipoSqlByTipoId = useMemo(() => {
        const map = new Map();
        (lookupOptions?.tipiCampiLookup || []).forEach((t) => {
            map.set(t.value, String(t?.original?.TIP_CodiceTipoSql || '').toLowerCase());
        });
        return map;
    }, [lookupOptions]);

    useEffect(() => {
        if (!isOpen) return;
        const sorted = [...(initialCampi || [])].sort(
            (a, b) => (a.CAM_Sequenza || 0) - (b.CAM_Sequenza || 0)
        );
        setDisplayCampi(sorted);
    }, [isOpen, initialCampi]);

    // ---- helpers ---------------------------------------------------------

    const nextSequenzaDominio = useCallback(() => {
        const max = displayCampi
            .filter((c) => Number(c.CAM_Sequenza) > 0 && Number(c.CAM_Sequenza) <= 1000)
            .reduce((m, c) => Math.max(m, Number(c.CAM_Sequenza) || 0), 0);
        return max + 10;
    }, [displayCampi]);

    const tipoIdSelezionato = formData.CAM_Id_TipiCampi;
    const tipoSqlSelezionato = tipoSqlByTipoId.get(Number(tipoIdSelezionato))
        || tipoSqlByTipoId.get(String(tipoIdSelezionato))
        || '';
    const lunghezzaRichiesta = TIPI_CON_LUNGHEZZA.has(tipoSqlSelezionato);
    const sequenzaNum = Number(formData.CAM_Sequenza) || 0;
    const isCampoDominio = sequenzaNum > 0 && sequenzaNum <= 1000;

    const loadOperatoriForCampo = useCallback(async (campoId) => {
        if (!campoId || campoId < 0) {
            setOperatoriRows([]);
            setOriginalOperatoriIds([]);
            return;
        }
        setIsLoadingOperatori(true);
        try {
            const resp = await apiService.get('/crud/SKE_CampiOperatori', {
                params: buildCampiOperatoriRequestParams(campoId),
            });
            const items = normalizeCampiOperatoriResponse(resp);
            setOperatoriRows(items);
            setOriginalOperatoriIds(items.map((r) => r.COP_Id_CampiOperatori).filter(Boolean));
        } catch (err) {
            console.warn('[CampiTableModalHub] Errore caricamento operatori:', err);
            setOperatoriRows([]);
            setOriginalOperatoriIds([]);
        } finally {
            setIsLoadingOperatori(false);
        }
    }, []);

    // ---- validazione -----------------------------------------------------

    const validate = useCallback(() => {
        const e = {};
        if (!formData.CAM_Nome || !formData.CAM_Nome.trim()) {
            e.CAM_Nome = 'Obbligatorio';
        } else if (!SNAKE_CASE_RE.test(formData.CAM_Nome)) {
            e.CAM_Nome = 'Usa snake_case: minuscolo, cifre e _ (deve iniziare con lettera)';
        }
        if (!formData.CAM_Id_TipiCampi) e.CAM_Id_TipiCampi = 'Obbligatorio';
        if (lunghezzaRichiesta && (formData.CAM_Lunghezza === '' || formData.CAM_Lunghezza === null)) {
            e.CAM_Lunghezza = 'Obbligatoria per varchar/char/decimal';
        }
        if (formData.CAM_Sequenza === '' || formData.CAM_Sequenza === null) {
            e.CAM_Sequenza = 'Obbligatoria';
        }
        if (isCampoDominio) {
            if (!formData.CAM_NomeUi || !formData.CAM_NomeUi.trim()) {
                e.CAM_NomeUi = 'Obbligatorio per i campi dominio (sequenza ≤ 1000)';
            }
            if (!toBoolFlag(formData.CAM_Nullo)) {
                if (!formData.CAM_ErroreValidazione || !formData.CAM_ErroreValidazione.trim()) {
                    e.CAM_ErroreValidazione = 'Obbligatorio per i campi NOT NULL del dominio';
                }
            }
        }
        if (toBoolFlag(formData.CAM_Cercabile)) {
            if (operatoriRows.length === 0) {
                e._operatori = 'Un campo cercabile deve esporre almeno un operatore';
            } else {
                // controllo duplicati (op + suff)
                const seen = new Set();
                for (const r of operatoriRows) {
                    if (!r.COP_Operatore || !OPERATORI_VALIDI.includes(r.COP_Operatore)) {
                        e._operatori = 'Operatore mancante o non valido in una riga';
                        break;
                    }
                    const k = `${r.COP_Operatore}|${(r.COP_SuffissoFiltro || '').trim().toLowerCase()}`;
                    if (seen.has(k)) {
                        e._operatori = `Coppia (${r.COP_Operatore}, "${r.COP_SuffissoFiltro || ''}") duplicata`;
                        break;
                    }
                    seen.add(k);
                }
            }
        }
        return e;
    }, [formData, lunghezzaRichiesta, isCampoDominio, operatoriRows]);

    // ---- azioni UI -------------------------------------------------------

    const startAdd = () => {
        setIsAdding(true);
        setEditingId(null);
        setFormData({
            ...emptyForm(),
            CAM_Sequenza: nextSequenzaDominio(),
        });
        setOperatoriRows([]);
        setOriginalOperatoriIds([]);
        setErrors({});
    };

    const startEdit = async (campo) => {
        setIsAdding(false);
        setEditingId(campo.CAM_Id_Campi);
        setFormData({
            CAM_Id_Campi: campo.CAM_Id_Campi,
            CAM_Nome: campo.CAM_Nome || '',
            CAM_Descrizione: campo.CAM_Descrizione || '',
            CAM_Id_TipiCampi: campo.CAM_Id_TipiCampi || '',
            CAM_Lunghezza: campo.CAM_Lunghezza ?? '',
            CAM_Sequenza: campo.CAM_Sequenza ?? '',
            CAM_Nullo: !!campo.CAM_Nullo,
            CAM_Default: campo.CAM_Default || '',
            CAM_ChiaveNaturale: !!campo.CAM_ChiaveNaturale,
            CAM_Cercabile: !!campo.CAM_Cercabile,
            CAM_Versioned: !!campo.CAM_Versioned,
            CAM_Cifrato: !!campo.CAM_Cifrato,
            CAM_NomeUi: campo.CAM_NomeUi || '',
            CAM_ErroreValidazione: campo.CAM_ErroreValidazione || '',
        });
        setErrors({});
        await loadOperatoriForCampo(campo.CAM_Id_Campi);
    };

    const cancelForm = () => {
        setIsAdding(false);
        setEditingId(null);
        setFormData(emptyForm());
        setOperatoriRows([]);
        setOriginalOperatoriIds([]);
        setErrors({});
    };

    // ---- salvataggio -----------------------------------------------------

    const buildCampoPayload = () => ({
        ...HARDCODED_DEFAULTS,
        ...(editingId ? { CAM_Id_Campi: editingId } : {}),
        CAM_Id_Tabelle: currentTableId,
        CAM_Nome: formData.CAM_Nome.trim(),
        CAM_Descrizione: formData.CAM_Descrizione || null,
        CAM_Id_TipiCampi: Number(formData.CAM_Id_TipiCampi),
        CAM_Lunghezza: lunghezzaRichiesta ? Number(formData.CAM_Lunghezza) : null,
        CAM_Sequenza: Number(formData.CAM_Sequenza),
        CAM_Nullo: toBoolFlag(formData.CAM_Nullo),
        CAM_Default: formData.CAM_Default || null,
        CAM_ChiaveNaturale: toBoolFlag(formData.CAM_ChiaveNaturale),
        CAM_Cercabile: toBoolFlag(formData.CAM_Cercabile),
        CAM_Versioned: toBoolFlag(formData.CAM_Versioned),
        CAM_Cifrato: toBoolFlag(formData.CAM_Cifrato),
        CAM_NomeUi: isCampoDominio ? (formData.CAM_NomeUi || '').trim() : (formData.CAM_NomeUi || null),
        CAM_ErroreValidazione: (formData.CAM_ErroreValidazione || '').trim() || null,
        CAM_StatiRecord: 'A',
    });

    const persistOperatori = async (campoId) => {
        // Wipe & rewrite: DELETE delle righe originali non più presenti,
        // CREATE delle nuove (id mancante), UPDATE di quelle esistenti.
        const currentIds = new Set(
            operatoriRows.map((r) => r.COP_Id_CampiOperatori).filter(Boolean)
        );
        const toDelete = originalOperatoriIds.filter((id) => !currentIds.has(id));
        const changes = [];

        for (const r of operatoriRows) {
            const base = {
                COP_Id_Campi: campoId,
                COP_Operatore: r.COP_Operatore,
                COP_SuffissoFiltro: (r.COP_SuffissoFiltro || '').trim() || null,
                COP_Sequenza: Number(r.COP_Sequenza) || 10,
            };
            if (r.COP_Id_CampiOperatori) {
                changes.push({
                    operation: 'update',
                    data: { COP_Id_CampiOperatori: r.COP_Id_CampiOperatori, ...base },
                });
            } else {
                changes.push({ operation: 'create', data: base });
            }
        }
        for (const id of toDelete) {
            changes.push({
                operation: 'delete',
                data: { COP_Id_CampiOperatori: id },
            });
        }
        if (changes.length === 0) return;
        await apiService.post('/crud/SKE_CampiOperatori', { changes });
    };

    const handleSave = async () => {
        const eMap = validate();
        if (Object.keys(eMap).length > 0) {
            setErrors(eMap);
            showError('Verifica i campi evidenziati');
            return;
        }
        setIsSaving(true);
        try {
            const payload = buildCampoPayload();
            const resp = await apiService.post('/crud/SKE_Campi', {
                changes: [
                    {
                        operation: editingId ? 'update' : 'create',
                        data: payload,
                    },
                ],
            });
            const ok = resp?.results?.success;
            if (!ok) throw new Error('Salvataggio campo fallito');

            // Recupera id (sia su create che su update).
            let campoId = editingId;
            if (!campoId) {
                const created = resp?.results?.results?.[0];
                campoId = created?.id || created?.insertId || created?.CAM_Id_Campi;
            }
            if (!campoId) throw new Error('ID campo non determinabile dopo il salvataggio');

            // Operatori (se cercabile o se esistono righe da pulire).
            if (toBoolFlag(formData.CAM_Cercabile) || originalOperatoriIds.length > 0) {
                await persistOperatori(campoId);
            }

            showSuccess(editingId ? 'Campo aggiornato' : 'Campo creato');
            cancelForm();
            onCampiUpdated && onCampiUpdated();
        } catch (err) {
            console.error('[CampiTableModalHub] save error:', err);
            showError(`Errore salvataggio: ${err?.message || err}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (campo) => {
        if (!window.confirm(`Eliminare il campo "${campo.CAM_Nome}"?`)) return;
        try {
            await apiService.post('/crud/SKE_Campi', {
                changes: [{ operation: 'delete', data: { CAM_Id_Campi: campo.CAM_Id_Campi } }],
            });
            showSuccess('Campo eliminato');
            onCampiUpdated && onCampiUpdated();
        } catch (err) {
            showError(`Errore eliminazione: ${err?.message || err}`);
        }
    };

    const handleOrdinamentoSave = async (campiDaAggiornare) => {
        if (!campiDaAggiornare || campiDaAggiornare.length === 0) {
            showError('Nessun campo da aggiornare');
            return;
        }

        setIsSaving(true);
        try {
            const changes = campiDaAggiornare.map((campo) => ({
                operation: 'update',
                data: {
                    CAM_Id_Campi: campo.CAM_Id_Campi,
                    CAM_ProgressivoOrdinamento: campo.CAM_ProgressivoOrdinamento,
                    CAM_TipoOrdinamento: campo.CAM_TipoOrdinamento,
                },
            }));

            const response = await apiService.post('/crud/SKE_Campi', { changes });

            if (response && response.results && response.results.success) {
                showSuccess('Configurazione ordinamento salvata con successo!');
                const aggiornamentiById = new Map(
                    campiDaAggiornare.map((campo) => [campo.CAM_Id_Campi, campo])
                );
                setDisplayCampi((prev) => prev.map((campo) => {
                    const aggiornamento = aggiornamentiById.get(campo.CAM_Id_Campi);
                    if (!aggiornamento) return campo;
                    return {
                        ...campo,
                        CAM_ProgressivoOrdinamento: aggiornamento.CAM_ProgressivoOrdinamento,
                        CAM_TipoOrdinamento: aggiornamento.CAM_TipoOrdinamento,
                    };
                }));
                onCampiUpdated && onCampiUpdated();
                setIsOrdinamentoModalOpen(false);
            } else {
                showError('Errore durante il salvataggio della configurazione');
            }
        } catch (err) {
            console.error('Errore durante il salvataggio ordinamento:', err);
            showError(`Errore: ${err?.message || err}`);
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    // ---- render ----------------------------------------------------------

    const inputCls = (hasError) =>
        `w-full px-3 py-2 rounded border text-sm bg-white dark:bg-gray-700 dark:text-gray-100 ${hasError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} focus:outline-none focus:ring-2 focus:ring-emerald-400`;

    const switchRow = (label, key, opts = {}) => (
        <div className="flex items-center justify-between py-1">
            <label className="text-sm text-gray-700 dark:text-gray-200">{label}{opts.required && <span className="text-red-500"> *</span>}</label>
            <div className="flex space-x-3">
                <label className="text-xs flex items-center space-x-1">
                    <input
                        type="radio"
                        checked={!!formData[key]}
                        onChange={() => setFormData((f) => ({ ...f, [key]: true }))}
                    />
                    <span>Sì</span>
                </label>
                <label className="text-xs flex items-center space-x-1">
                    <input
                        type="radio"
                        checked={!formData[key]}
                        onChange={() => setFormData((f) => ({ ...f, [key]: false }))}
                    />
                    <span>No</span>
                </label>
            </div>
        </div>
    );

    return (
        <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">
                <div className="px-6 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-t-xl flex justify-between items-center">
                    <div>
                        <h2 className="text-lg font-bold">Campi tabella HUB (Milo)</h2>
                        <p className="text-xs opacity-90">Modulo HUB · solo metadati consumati dal generatore TypeScript Milo</p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded hover:bg-white/20"><FaTimes /></button>
                </div>

                <div className="p-4 overflow-y-auto flex-1 space-y-4">
                    {/* Lista campi esistenti */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Campi presenti ({displayCampi.length})
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setIsOrdinamentoModalOpen(true)}
                                    disabled={!currentTableId}
                                    className="inline-flex items-center text-xs px-2 py-1 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Configura l'ordinamento dei campi (CAM_ProgressivoOrdinamento e CAM_TipoOrdinamento)"
                                >
                                    <FaSortAmountDown className="mr-1" /> Orderby default
                                </button>
                                <button
                                    type="button"
                                    onClick={startAdd}
                                    className="inline-flex items-center text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                                >
                                    <FaPlus className="mr-1" /> Aggiungi Campo HUB
                                </button>
                            </div>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 sticky top-0 bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="text-left py-1 px-2 w-16">Seq</th>
                                        <th className="text-left py-1 px-2">Nome</th>
                                        <th className="text-left py-1 px-2">Tipo</th>
                                        <th className="text-left py-1 px-2 w-16">Null</th>
                                        <th className="text-left py-1 px-2 w-16">Cerc.</th>
                                        <th className="text-left py-1 px-2 w-16">Vers.</th>
                                        <th className="text-left py-1 px-2 w-16">Cifr.</th>
                                        <th className="w-24" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayCampi.length === 0 && (
                                        <tr><td colSpan={8} className="text-center text-xs text-gray-500 py-4">Nessun campo presente</td></tr>
                                    )}
                                    {displayCampi.map((c) => (
                                        <tr key={c.CAM_Id_Campi} className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                            <td className="py-1 px-2">{c.CAM_Sequenza}</td>
                                            <td className="py-1 px-2 font-mono">{c.CAM_Nome}</td>
                                            <td className="py-1 px-2 text-gray-600 dark:text-gray-300">
                                                {(lookupOptions?.tipiCampiLookup || []).find((t) => t.value === c.CAM_Id_TipiCampi)?.label || c.CAM_Id_TipiCampi}
                                            </td>
                                            <td className="py-1 px-2">{c.CAM_Nullo ? 'Sì' : 'No'}</td>
                                            <td className="py-1 px-2">{c.CAM_Cercabile ? 'Sì' : '—'}</td>
                                            <td className="py-1 px-2">{c.CAM_Versioned ? 'Sì' : '—'}</td>
                                            <td className="py-1 px-2">{c.CAM_Cifrato ? 'Sì' : '—'}</td>
                                            <td className="py-1 px-2 text-right">
                                                <button onClick={() => startEdit(c)} className="text-blue-600 hover:text-blue-800 mr-2" title="Modifica"><FaEdit /></button>
                                                <button onClick={() => handleDelete(c)} className="text-red-600 hover:text-red-800" title="Elimina"><FaTrash /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Form Add/Edit */}
                    {(isAdding || editingId) && (
                        <div className="border border-emerald-300 dark:border-emerald-700 rounded-lg p-4 bg-emerald-50/40 dark:bg-emerald-900/10">
                            <h3 className="text-sm font-bold mb-3 text-gray-800 dark:text-gray-100">
                                {editingId ? `Modifica campo "${formData.CAM_Nome || ''}"` : 'Aggiungi Campo HUB (Milo)'}
                            </h3>

                            {isLoadingLookups && (
                                <div className="text-xs text-gray-500 mb-2"><FaSpinner className="inline animate-spin mr-1" /> Caricamento tipi…</div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">Nome Campo *</label>
                                    <input
                                        type="text"
                                        value={formData.CAM_Nome}
                                        onChange={(e) => setFormData((f) => ({ ...f, CAM_Nome: e.target.value }))}
                                        placeholder="es. ragione_sociale"
                                        className={inputCls(errors.CAM_Nome)}
                                        maxLength={100}
                                    />
                                    {errors.CAM_Nome && <p className="text-xs text-red-600 mt-1">{errors.CAM_Nome}</p>}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">Descrizione</label>
                                    <input
                                        type="text"
                                        value={formData.CAM_Descrizione}
                                        onChange={(e) => setFormData((f) => ({ ...f, CAM_Descrizione: e.target.value }))}
                                        className={inputCls(false)}
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">Tipo *</label>
                                    <select
                                        value={formData.CAM_Id_TipiCampi}
                                        onChange={(e) => setFormData((f) => ({ ...f, CAM_Id_TipiCampi: e.target.value }))}
                                        className={inputCls(errors.CAM_Id_TipiCampi)}
                                    >
                                        <option value="">-- Seleziona --</option>
                                        {tipiCampiOptions.map((t) => (
                                            <option key={t.value} value={t.value}>
                                                {t.label} {t?.original?.TIP_CodiceTipoSql ? `(${t.original.TIP_CodiceTipoSql})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.CAM_Id_TipiCampi && <p className="text-xs text-red-600 mt-1">{errors.CAM_Id_TipiCampi}</p>}
                                </div>

                                {lunghezzaRichiesta && (
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">Lunghezza *</label>
                                        <input
                                            type="number"
                                            min={1}
                                            value={formData.CAM_Lunghezza}
                                            onChange={(e) => setFormData((f) => ({ ...f, CAM_Lunghezza: e.target.value }))}
                                            className={inputCls(errors.CAM_Lunghezza)}
                                        />
                                        {errors.CAM_Lunghezza && <p className="text-xs text-red-600 mt-1">{errors.CAM_Lunghezza}</p>}
                                    </div>
                                )}

                                <div>
                                    <label
                                        className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1"
                                        title="Convenzione MILO: 1-1000 = campo dominio (UI), >1000 = campo tecnico (audit/SCD2)"
                                    >
                                        Sequenza * <FaInfoCircle className="inline text-gray-400" />
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.CAM_Sequenza}
                                        onChange={(e) => setFormData((f) => ({ ...f, CAM_Sequenza: e.target.value }))}
                                        className={inputCls(errors.CAM_Sequenza)}
                                    />
                                    {errors.CAM_Sequenza && <p className="text-xs text-red-600 mt-1">{errors.CAM_Sequenza}</p>}
                                    <p className="text-[10px] text-gray-500 mt-1">
                                        {isCampoDominio ? 'Campo DOMINIO (verrà emesso nei template UI Milo)' : 'Campo TECNICO (>1000): non emesso nei template'}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="md:col-span-2 text-xs uppercase font-semibold text-gray-500 mb-1">Flag dominio</div>
                                {switchRow('Nullable', 'CAM_Nullo', { required: true })}
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">Default</label>
                                    <input
                                        type="text"
                                        value={formData.CAM_Default}
                                        onChange={(e) => setFormData((f) => ({ ...f, CAM_Default: e.target.value }))}
                                        className={inputCls(false)}
                                    />
                                </div>
                                {switchRow('Chiave naturale', 'CAM_ChiaveNaturale', { required: true })}
                                {switchRow('Cercabile', 'CAM_Cercabile', { required: true })}
                                {switchRow('Versioned (SCD2)', 'CAM_Versioned', { required: true })}
                                {switchRow('Cifrato', 'CAM_Cifrato', { required: true })}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="md:col-span-2 text-xs uppercase font-semibold text-gray-500 mb-1">Etichette UI</div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                        Nome UI {isCampoDominio && <span className="text-red-500">*</span>}
                                    </label>
                                    <input
                                        type="text"
                                        maxLength={100}
                                        value={formData.CAM_NomeUi}
                                        onChange={(e) => setFormData((f) => ({ ...f, CAM_NomeUi: e.target.value }))}
                                        className={inputCls(errors.CAM_NomeUi)}
                                    />
                                    {errors.CAM_NomeUi && <p className="text-xs text-red-600 mt-1">{errors.CAM_NomeUi}</p>}
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                        Messaggio errore {isCampoDominio && !toBoolFlag(formData.CAM_Nullo) && <span className="text-red-500">*</span>}
                                    </label>
                                    <input
                                        type="text"
                                        maxLength={200}
                                        value={formData.CAM_ErroreValidazione}
                                        onChange={(e) => setFormData((f) => ({ ...f, CAM_ErroreValidazione: e.target.value }))}
                                        className={inputCls(errors.CAM_ErroreValidazione)}
                                    />
                                    {errors.CAM_ErroreValidazione && <p className="text-xs text-red-600 mt-1">{errors.CAM_ErroreValidazione}</p>}
                                </div>
                            </div>

                            {/* Sub-editor operatori */}
                            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="text-xs uppercase font-semibold text-gray-500 mb-1">
                                    Operatori filtro {toBoolFlag(formData.CAM_Cercabile) ? <span className="text-red-500">*</span> : null}
                                </div>
                                {isLoadingOperatori ? (
                                    <div className="text-xs text-gray-500"><FaSpinner className="inline animate-spin mr-1" /> Caricamento operatori…</div>
                                ) : (
                                    <CampiOperatoriSubEditor
                                        value={operatoriRows}
                                        onChange={setOperatoriRows}
                                        cercabile={!!formData.CAM_Cercabile}
                                    />
                                )}
                                {errors._operatori && <p className="text-xs text-red-600 mt-1">{errors._operatori}</p>}
                            </div>

                            <div className="mt-4 flex justify-end space-x-2">
                                <button
                                    onClick={cancelForm}
                                    disabled={isSaving}
                                    className="px-3 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    <FaBan className="inline mr-1" /> Annulla
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="px-4 py-2 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                                >
                                    {isSaving ? <FaSpinner className="inline animate-spin mr-1" /> : <FaSave className="inline mr-1" />}
                                    Salva
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                        Chiudi
                    </button>
                </div>
            </div>

            {isOrdinamentoModalOpen && (
                <OrdinamentoCampiModal
                    isOpen={isOrdinamentoModalOpen}
                    onClose={() => setIsOrdinamentoModalOpen(false)}
                    campi={displayCampi}
                    onSave={handleOrdinamentoSave}
                    themeColor="emerald"
                />
            )}
        </div>
    );
};

export default CampiTableModalHub;
